import {
  createContext,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useRef,
} from "react";
import { Product } from "../../types/types";
import {
  createProductListEventDetail,
  dispatchCustomEvent,
} from "../../utils/events/dispatchEvent";
import { useVariant } from "../VariantContext/VariantContext";
import { useProductCardMetadata } from "../ProductCardMetadataContext/ProductCardMetadataContext";
import { EventName, productCardEvents } from "./productCardEvents";

type ProductCardEventContextType = {
  product: Product;
  index: number;
  eventsRef: RefObject<HTMLElement | null>;
  variantId?: string;
  skuId?: string;
};

const ProductCardEventContext =
  createContext<ProductCardEventContextType | null>(null);

type Props = ProductCardEventContextType & {
  children: ReactNode;
};

export function ProductCardEventProvider(props: Props): React.JSX.Element {
  const { variant } = useVariant();
  const { instanceId } = useProductCardMetadata();
  const hasMounted = useRef(false);
  const variantId = variant?.id;
  const { children, product, index, eventsRef, ...nonChildrenProps } = props;

  const skuId = variant?.aliases?.skuId;

  const latestCardDetails = useRef({ product, index, instanceId, eventsRef });
  latestCardDetails.current = { product, index, instanceId, eventsRef };

  useEffect(
    function dispatchVariantChangedEvent() {
      if (!hasMounted.current) {
        hasMounted.current = true;
        return;
      }

      if (!variantId) {
        return;
      }

      const { product, index, instanceId, eventsRef } =
        latestCardDetails.current;

      dispatchCustomEvent(
        eventsRef,
        productCardEvents.variantChanged,
        createProductListEventDetail({
          type: productCardEvents.variantChanged,
          product,
          index,
          instanceId,
          variantId,
          skuId,
        }),
      );
    },
    [variantId, skuId],
  );

  return (
    <ProductCardEventContext.Provider
      value={{
        ...nonChildrenProps,
        product,
        index,
        eventsRef,
        variantId,
        skuId,
      }}
    >
      {children}
    </ProductCardEventContext.Provider>
  );
}

export function useProductCardEvent() {
  const context = useContext(ProductCardEventContext);

  if (!context) {
    throw new Error(
      "useProductCardEvent must be used within a ProductCardEventProvider",
    );
  }

  const { product, index, variantId, skuId } = context;
  const { instanceId } = useProductCardMetadata();

  const standardDetail = createProductListEventDetail({
    type: "",
    product,
    index,
    quantity: 1,
    instanceId,
    variantId,
    skuId,
  });

  return (type: EventName, customDetail?: Record<string, unknown>) => {
    dispatchCustomEvent(context.eventsRef, type, {
      ...standardDetail,
      ...customDetail,
    });
  };
}
